import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { RequirePermissions } from "src/routes/authentication";
import { LabelCoveragePriority } from "src/store/api/generatedApi";
import {
  type BaseStrategy,
  decomposePriority,
  composePriority,
} from "components/manageLabellers/reservationSettings/labelCoveragePriorityUtils";
import IdentifyEventsAdminFields from "./IdentifyEventsAdminFields";

type IdentifyEventsLabellerOptionsProps = Readonly<{
  overrideSettings: boolean;
  setOverrideSettings: (value: boolean) => void;
  reserveFrom: Dayjs | null;
  setReserveFrom: (date: Dayjs | null) => void;
  reserveUntil: Dayjs | null;
  setReserveUntil: (date: Dayjs | null) => void;
  labelCoveragePriority: LabelCoveragePriority;
  setLabelCoveragePriority: (priority: LabelCoveragePriority) => void;
  onResetAll: () => void;
  hasNonDefaultValues: boolean;
  location: string;
  requiredAngles: readonly string[];
  setRequiredAngles: (angles: string[]) => void;
  maxDurationSeconds: number;
  setMaxDurationSeconds: (seconds: number) => void;
  allowedCoverageGapSeconds: number;
  setAllowedCoverageGapSeconds: (seconds: number) => void;
  ignoreCoverage: boolean;
  setIgnoreCoverage: (value: boolean) => void;
}>;

export default function IdentifyEventsLabellerOptions({
  overrideSettings,
  setOverrideSettings,
  reserveFrom,
  setReserveFrom,
  reserveUntil,
  setReserveUntil,
  labelCoveragePriority,
  setLabelCoveragePriority,
  onResetAll,
  hasNonDefaultValues,
  location,
  requiredAngles,
  setRequiredAngles,
  maxDurationSeconds,
  setMaxDurationSeconds,
  allowedCoverageGapSeconds,
  setAllowedCoverageGapSeconds,
  ignoreCoverage,
  setIgnoreCoverage,
}: IdentifyEventsLabellerOptionsProps) {
  const { t } = useTenantTranslation();

  const { labelRecent, baseStrategy } = decomposePriority(
    labelCoveragePriority
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Typography variant="h6">{t("labellingOptions")}</Typography>
          {overrideSettings ? (
            <IconButton
              component="span"
              role="button"
              tabIndex={0}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onResetAll();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onResetAll();
                }
              }}
              color="warning"
              disabled={!hasNonDefaultValues}
              sx={{
                visibility: hasNonDefaultValues ? "visible" : "hidden",
              }}
            >
              <RestartAltIcon />
            </IconButton>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={overrideSettings}
                onChange={(e) => {
                  setOverrideSettings(e.target.checked);
                }}
              />
            }
            label={t("overrideAssignedSettings")}
          />

          {overrideSettings ? (
            <>
              <Stack direction="row" spacing={2}>
                <SafeDateTimePicker
                  label={t("reserveFrom")}
                  value={reserveFrom}
                  onChange={(newValue) => {
                    if (newValue) setReserveFrom(newValue);
                  }}
                  format="YYYY-MM-DD HH:mm:ss"
                  timezone="UTC"
                  enableAccessibleFieldDOMStructure={false}
                />
                <SafeDateTimePicker
                  label={t("reserveUntil")}
                  value={reserveUntil}
                  onChange={(newValue) => {
                    if (newValue) setReserveUntil(newValue);
                  }}
                  format="YYYY-MM-DD HH:mm:ss"
                  timezone="UTC"
                  enableAccessibleFieldDOMStructure={false}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  {t("labellingPriority")}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={labelRecent}
                      onChange={(e) => {
                        setLabelCoveragePriority(
                          composePriority(e.target.checked, baseStrategy)
                        );
                      }}
                    />
                  }
                  label={t("labelRecentFirst")}
                />
                <ToggleButtonGroup
                  color="primary"
                  value={baseStrategy}
                  exclusive
                  onChange={(_, newValue: BaseStrategy | null) => {
                    if (newValue)
                      setLabelCoveragePriority(
                        composePriority(labelRecent, newValue)
                      );
                  }}
                >
                  <ToggleButton value="breadth">
                    {t("prioritiseUnlabelledFootage")}
                  </ToggleButton>
                  <ToggleButton value="depth">
                    {t("prioritiseMultiplePasses")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <RequirePermissions permissions={["video_label_reviewing"]}>
                <Divider />
                <Typography variant="subtitle2">{t("adminOptions")}</Typography>
                <IdentifyEventsAdminFields
                  location={location}
                  requiredAngles={requiredAngles}
                  setRequiredAngles={setRequiredAngles}
                  maxDurationSeconds={maxDurationSeconds}
                  setMaxDurationSeconds={setMaxDurationSeconds}
                  allowedCoverageGapSeconds={allowedCoverageGapSeconds}
                  setAllowedCoverageGapSeconds={setAllowedCoverageGapSeconds}
                  ignoreCoverage={ignoreCoverage}
                  setIgnoreCoverage={setIgnoreCoverage}
                />
              </RequirePermissions>
            </>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
